import {
  Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialogRef,
} from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { CoursesService } from 'src/app/services/courses.service';
import { EntityService } from 'src/app/services/entity.service';
import { UsersService } from 'src/app/services/users.service';

/** API push definition name for the quick-people / selected-users push type */
const SELECTED_USERS_PUSH_NAME = 'Course push to selected users';

const DUPLICATE_PUSH_TYPE_LABELS: Record<string, string> = {
  COURSE_PUSH_TO_BUILDING_USERS: 'Push to a specific building',
  COURSE_PUSH_TO_ENTITY_USERS: 'Push to entire district',
  COURSE_PUSH_TO_POSITION_USERS: 'Push to a specific position',
  COURSE_PUSH_TO_POSITION_BUILDING_USERS: 'Push to a specific position at a specific building',
};

type WorkflowStep =
  | 'select-dates'
  | 'select-push-params'
  | 'select-requirement'
  | 'select-buildings-new'
  | 'select-positions'
  | 'select-users'
  | 'send-message'
  | 'confirm'
  | 'submitting'
  | 'success'
  | 'error';

type PushOption = 'copy-existing' | 'select-new' | null;

interface DateConflict {
  date: string;
  reason: string;
  code?: string;
}

interface DuplicatedCourseRecord {
  date: string;
  courseId: number;
  courseItemId?: number;
  groupAssigned: boolean;
  coursePushId?: number;
}

export interface DateTimeEntry {
  date: string;
  startTime: string | null; 
  endTime: string | null;  
}

/**
 * Multi-step modal that drives the "Duplicate & Push" workflow:
 *  1. Pick one or more future dates + set start/end times.
 *  2a. (copy-existing) Go straight to Confirm.
 *  2b. (select-new) Follow the same wizard as Group Push:
 *        Push Type → Requirement → [Buildings] → [Positions] → Message → Confirm.
 *  3. Submit and surface success / partial-failure / error feedback.
 */
@Component({
  selector: 'app-duplicate-and-push',
  templateUrl: './duplicate-and-push.component.html',
  styleUrls: [ './duplicate-and-push.component.scss' ],
})
export class DuplicateAndPushComponent implements OnInit, OnDestroy {
  @ViewChild('buildingsinput') buildingsinput: any;
  @ViewChild('positionsinput') positionsinput: any;
  @ViewChild('userinput') userinput: any;

  @Input() completeCourseSessionDetails: any;
  @Output() backToPrimaryPushList = new EventEmitter<void>();

  currentStep: WorkflowStep = 'select-dates';
  pushOption: PushOption = null;

  // ── Stepper state (mirrors push-to-group) ───────────────────────────
  steps: { completed: boolean; text: string }[] = [
    { completed: false, text: 'Select Dates' },
    { completed: false, text: 'Confirm' },
  ];
  selectedStep: { completed: boolean; text: string } = this.steps[0];
  screenArray: WorkflowStep[] = [ 'select-dates', 'confirm' ];

  // ── Select-new: push params (mirrors push-to-group state) ───────────
  pushGroups: any[] = [];
  selectedPushGroup: string | null = null;
  coursePushPackage: any = {
    buildings: [], requirement: {}, positions: [], users: [], messageSubject: '', messageContent: '',
  };
  formattedCourseRequirements: any[] = [];
  entityBuildings: any[] = [];
  entityPositions: any[] = [];
  users: any[] = [];

  // ── Step 1 — date + time entries ────────────────────────────────────
  selectedDates: string[] = [];
  dateEntries: DateTimeEntry[] = [];

  // ── Submit state ─────────────────────────────────────────────────────
  isSubmitting = false;
  submitErrorMessage = '';
  submitErrorDetails: DateConflict[] = [];
  duplicatedCourses: DuplicatedCourseRecord[] = [];
  resultErrorCode: string | number | null = null;
  inactiveUsersSkipped = 0;

  // ── Capacity warning ──────────────────────────────────────────────────
  capacityWarningAcknowledged = false;

  /**
   * Earliest selectable date for the calendar (YYYY-MM-DD), derived from the
   * active school year's start_date in ks_entity_years.  Empty string until the
   * entity data loads; the calendar defaults to today in that case.
   */
  schoolYearStartDate: string = '';

  private submitSubscription?: Subscription;
  private pushDefSubscription?: Subscription;
  private entitySubscription?: Subscription;
  private userDataSubscription?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<unknown>,
    private coursesService: CoursesService,
    private entityService: EntityService,
    private usersService: UsersService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit(): void {
    this.completeCourseSessionDetails =
      this.completeCourseSessionDetails || this.data?.completeCourseSessionDetails;

    // Load push groups (same as push-to-group)
    this.coursesService.getCoursePushGroups();
    this.pushDefSubscription = this.coursesService.pushDefinitions$.subscribe((pushDefinitions: any) => {
      if (!pushDefinitions) return;
      const rows = pushDefinitions?.rows ?? [];
      this.pushGroups = this.sortDuplicatePushGroups(rows);
      if (!this.selectedPushGroup && this.pushGroups.length > 0) {
        this.selectedPushGroup = this.pushGroups[0].kalpaIdentifier;
      }
    });

    const userListParams = {
      userNameFilter: '',
      activeFlag: 1 as 0 | 1,
      sortColumn: 'lastName',
      sortDirection: 'ASC' as 'ASC' | 'DESC',
      offset: 0,
      includedAssociations: 'Buildings, Positions',
    };
    this.usersService.getUsers(userListParams);
    this.userDataSubscription = this.usersService.usersData$.subscribe((data: any) => {
      if (!data) return;
      this.users = data.rows ?? [];
    });

    // Load entity buildings, positions, and current school year for the date picker.
    this.entityService.getEntity('Buildings, Positions, CurrentYear');
    this.entitySubscription = this.entityService.entityData$.subscribe((entityData: any) => {
      this.entityBuildings = entityData?.Buildings ?? [];
      this.entityPositions = entityData?.Positions ?? [];

      // Derive the earliest selectable date from the active school year's start_date.
      const rawStart = entityData?.CurrentYear?.startDate;
      if (rawStart) {
        const parsed = new Date(rawStart);
        if (!Number.isNaN(parsed.getTime())) {
          this.schoolYearStartDate = parsed.toISOString().slice(0, 10);
        }
      }
    });

    // Format requirements for the select dropdown
    this.formattedCourseRequirements = (
      this.completeCourseSessionDetails?.Course?.Requirements ?? []
    ).map((r: any) => ({ text: r.requirement, value: r.requirementId }));
    this.formattedCourseRequirements.unshift({ value: 'portfolio', text: 'Portfolio/Unassigned' });
  }

  ngOnDestroy(): void {
    this.submitSubscription?.unsubscribe();
    this.pushDefSubscription?.unsubscribe();
    this.entitySubscription?.unsubscribe();
    this.userDataSubscription?.unsubscribe();
  }

  // #region accessors
  get sourceCourseName(): string {
    return this.completeCourseSessionDetails?.Course?.courseName || 'Course';
  }

  get sourceCourseId(): number | null {
    const id = this.completeCourseSessionDetails?.Course?.id ?? this.completeCourseSessionDetails?.courseId;
    return id ? Number(id) : null;
  }

  /** True when the source session is tagged Async or Anytime. */
  get sourceIsAsyncOrAnytime(): boolean {
    const option = String(this.completeCourseSessionDetails?.preRecordedOption ?? '').toUpperCase();
    return option === 'ASYNC' || option === 'ANYTIME';
  }

  get sortedSelectedDates(): string[] {
    return [ ...this.selectedDates ].sort();
  }

  entriesForDate = (date: string): DateTimeEntry[] =>
    this.dateEntries.filter((e) => e.date === date);

  sessionCountForDate = (date: string): number =>
    this.dateEntries.filter((e) => e.date === date).length;

  isLastEntryForDate = (index: number, entries: DateTimeEntry[]): boolean => {
    const current = entries[index];
    const next = entries[index + 1];
    return !next || next.date !== current.date;
  };

  get isOnTerminalStep(): boolean {
    return [ 'submitting', 'success', 'error' ].includes(this.currentStep);
  }

  get sortedDateEntries(): DateTimeEntry[] {
    return [ ...this.dateEntries ].sort((a, b) => {
      const dc = a.date.localeCompare(b.date);
      if (dc !== 0) return dc;
      // Within the same date, sort earlier start times first.
      if (a.startTime && b.startTime) return a.startTime.localeCompare(b.startTime);
      if (a.startTime) return -1;
      if (b.startTime) return 1;
      return 0;
    });
  }

  get areAllDateTimesComplete(): boolean {
    return this.dateEntries.length > 0 && this.dateEntries.every((entry) => this.isDateEntryTimeComplete(entry));
  }

  get hasIncompleteDateTimes(): boolean {
    return this.dateEntries.length > 0 && !this.areAllDateTimesComplete;
  }

  /** Numeric course capacity (null when unlimited or not set). */
  get courseCapacity(): number | null {
    const raw =
      this.completeCourseSessionDetails?.courseSize
      ?? this.completeCourseSessionDetails?.Course?.courseSize;
    const n = Number(raw);
    return raw != null && !isNaN(n) && n > 0 ? n : null;
  }

  /**
   * Estimated number of users this push will target, calculated client-side
   * to mirror the backend getUsersList logic.  Returns null when the push type
   * has no countable target yet (e.g. building not yet chosen).
   */
  get estimatedPushUserCount(): number | null {
    if (this.pushOption !== 'select-new') return null;

    if (this.isPushToSelectedUsersGroup()) {
      return this.coursePushPackage.users?.length ?? 0;
    }

    const selectedPositionIds = new Set<number>(
      (this.coursePushPackage.positions ?? []).map((p: any) => Number(p.id)),
    );
    const selectedBuildingId: number | null = this.coursePushPackage.buildings?.[0]?.id != null
      ? Number(this.coursePushPackage.buildings[0].id)
      : null;

    const userHasSelectedPosition = (u: any): boolean => {
      if (selectedPositionIds.size === 0) return false;
      if (selectedPositionIds.has(Number(u.primaryPositionId))) return true;
      const positions: any[] = u.Positions ?? u.positions ?? [];
      return positions.some((p: any) => selectedPositionIds.has(Number(p.id)));
    };

    const userInSelectedBuilding = (u: any): boolean => {
      if (selectedBuildingId === null) return false;
      if (Number(u.primaryBuildingId) === selectedBuildingId) return true;
      const buildings: any[] = u.Buildings ?? u.buildings ?? [];
      return buildings.some((b: any) => Number(b.id) === selectedBuildingId);
    };

    switch (this.selectedPushGroup) {
    case 'COURSE_PUSH_TO_ENTITY_USERS':
      return this.users.length;
    case 'COURSE_PUSH_TO_BUILDING_USERS':
      return selectedBuildingId !== null ? this.users.filter(userInSelectedBuilding).length : null;
    case 'COURSE_PUSH_TO_POSITION_USERS':
      return selectedPositionIds.size > 0 ? this.users.filter(userHasSelectedPosition).length : null;
    case 'COURSE_PUSH_TO_POSITION_BUILDING_USERS':
      return (selectedPositionIds.size > 0 && selectedBuildingId !== null)
        ? this.users.filter((u) => userHasSelectedPosition(u) && userInSelectedBuilding(u)).length
        : null;
    default:
      return null;
    }
  }

  /**
   * True when we have both a finite capacity and a push-user estimate that
   * exceeds it — shown as a warning on the Confirm step.
   */
  get hasCapacityWarning(): boolean {
    const capacity = this.courseCapacity;
    const pushCount = this.estimatedPushUserCount;
    return capacity !== null && pushCount !== null && pushCount > capacity;
  }

  isDateEntryTimeComplete = (entry: DateTimeEntry): boolean =>
    !!entry.startTime && /^\d{2}:\d{2}$/.test(entry.startTime)
    && !!entry.endTime && /^\d{2}:\d{2}$/.test(entry.endTime);

  formatEntryTime = (time: string | null): string => {
    if (!time) return '—';
    const [ hStr, mStr ] = time.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    const period = h < 12 ? 'AM' : 'PM';
    const hr = h % 12 || 12;
    return `${hr}:${String(m).padStart(2, '0')} ${period}`;
  };
  // #endregion accessors

  /** Display labels for duplicate & push (API names differ). */
  getPushGroupDisplayLabel = (group: any): string => {
    if (!group) return '';
    const kid = group.kalpaIdentifier as string;
    if (DUPLICATE_PUSH_TYPE_LABELS[kid]) return DUPLICATE_PUSH_TYPE_LABELS[kid];
    if (group.name === SELECTED_USERS_PUSH_NAME) return 'Push to an individual person';
    return group.name ?? kid ?? '';
  };

  getPushGroupName = (kid: string | null): string => {
    const group = this.pushGroups.find((g) => g.kalpaIdentifier === kid);
    return this.getPushGroupDisplayLabel(group) || kid || '';
  };

  isPushToSelectedUsersGroup = (): boolean =>
    this.pushGroups.find((g) => g.kalpaIdentifier === this.selectedPushGroup)?.name === SELECTED_USERS_PUSH_NAME;

  /**
   * For position+building push type: return only buildings that have at least
   * one user whose positions include any of the currently-selected positions.
   * Mirrors the backend filterUsersByBuildingAndPosition logic exactly.
   * For all other push types: return the full buildings list.
   */
  get filteredBuildings(): any[] {
    if (
      this.selectedPushGroup === 'COURSE_PUSH_TO_POSITION_BUILDING_USERS'
      && this.coursePushPackage.positions?.length > 0
    ) {
      const selectedPositionIds = new Set(
        this.coursePushPackage.positions.map((p: any) => Number(p.id)),
      );

      const userHasSelectedPosition = (u: any): boolean => {
        if (selectedPositionIds.has(Number(u.primaryPositionId))) return true;
        const secondary: any[] = u.Positions ?? u.positions ?? [];
        return secondary.some((p: any) => selectedPositionIds.has(Number(p.id)));
      };

      const matchingBuildingIds = new Set<number>();
      for (const u of this.users) {
        if (!userHasSelectedPosition(u)) continue;
        if (u.primaryBuildingId) matchingBuildingIds.add(Number(u.primaryBuildingId));
        const secondary: any[] = u.Buildings ?? u.buildings ?? [];
        secondary.forEach((b: any) => matchingBuildingIds.add(Number(b.id)));
      }

      if (matchingBuildingIds.size === 0) return this.entityBuildings;
      return this.entityBuildings.filter((b: any) => matchingBuildingIds.has(Number(b.id)));
    }
    return this.entityBuildings;
  }

  /** UI order: all “specific” targets together, then entire district (any unknown API types last). */
  private sortDuplicatePushGroups(rows: any[]): any[] {
    const specificKids = [
      'COURSE_PUSH_TO_BUILDING_USERS',
      'COURSE_PUSH_TO_POSITION_USERS',
      'COURSE_PUSH_TO_POSITION_BUILDING_USERS',
    ];
    const byKid = new Map(rows.map((g: any) => [ g.kalpaIdentifier, g ]));
    const ordered: any[] = [];

    specificKids.forEach((kid) => {
      const g = byKid.get(kid);
      if (g) ordered.push(g);
    });

    rows.filter((g: any) => g.name === SELECTED_USERS_PUSH_NAME).forEach((g) => ordered.push(g));

    const district = byKid.get('COURSE_PUSH_TO_ENTITY_USERS');
    if (district) ordered.push(district);

    rows.forEach((g: any) => {
      if (!ordered.includes(g)) ordered.push(g);
    });

    return ordered;
  }

  onPushGroupSelected = (): void => {
    if (this.isPushToSelectedUsersGroup()) {
      this.coursePushPackage = {
        ...this.coursePushPackage,
        buildings: [], positions: [], messageSubject: '',
      };
    } else {
      this.coursePushPackage = { ...this.coursePushPackage, users: [] };
    }
    this.updateWizard();
  };

  // #region wizard (mirrors push-to-group.updateWizard)
  /**
   * Rebuilds `screenArray` and `steps` based on the current `pushOption` and
   * `selectedPushGroup`.  Preserves the completed state of already-passed steps
   * so the stepper stays accurate when the user switches push group mid-wizard.
   */
  updateWizard = () => {
    if (this.pushOption === 'copy-existing') {
      this.screenArray = [ 'select-dates', 'confirm' ];
      this.steps = [
        { completed: false, text: 'Select Dates' },
        { completed: false, text: 'Confirm' },
      ];
    } else if (this.pushOption === 'select-new' && this.isPushToSelectedUsersGroup()) {
      this.screenArray = [
        'select-dates', 'select-push-params', 'select-requirement',
        'select-users', 'confirm',
      ];
      this.steps = [
        { completed: false, text: 'Select Dates' },
        { completed: false, text: 'Select Push Type' },
        { completed: false, text: 'Select Requirement' },
        { completed: false, text: 'Select User(s)' },
        { completed: false, text: 'Confirm' },
      ];
    } else {
      switch (this.selectedPushGroup) {
      case 'COURSE_PUSH_TO_BUILDING_USERS':
        this.screenArray = [
          'select-dates', 'select-push-params', 'select-requirement',
          'select-buildings-new', 'send-message', 'confirm',
        ];
        this.steps = [
          { completed: false, text: 'Select Dates' },
          { completed: false, text: 'Select Push Type' },
          { completed: false, text: 'Select Requirement' },
          { completed: false, text: 'Select Buildings' },
          { completed: false, text: 'Send Message' },
          { completed: false, text: 'Confirm' },
        ];
        break;
      case 'COURSE_PUSH_TO_ENTITY_USERS':
        this.screenArray = [
          'select-dates', 'select-push-params', 'select-requirement',
          'send-message', 'confirm',
        ];
        this.steps = [
          { completed: false, text: 'Select Dates' },
          { completed: false, text: 'Select Push Type' },
          { completed: false, text: 'Select Requirement' },
          { completed: false, text: 'Send Message' },
          { completed: false, text: 'Confirm' },
        ];
        break;
      case 'COURSE_PUSH_TO_POSITION_USERS':
        this.screenArray = [
          'select-dates', 'select-push-params', 'select-requirement',
          'select-positions', 'send-message', 'confirm',
        ];
        this.steps = [
          { completed: false, text: 'Select Dates' },
          { completed: false, text: 'Select Push Type' },
          { completed: false, text: 'Select Requirement' },
          { completed: false, text: 'Select Positions' },
          { completed: false, text: 'Send Message' },
          { completed: false, text: 'Confirm' },
        ];
        break;
      case 'COURSE_PUSH_TO_POSITION_BUILDING_USERS':
        this.screenArray = [
          'select-dates', 'select-push-params', 'select-requirement',
          'select-positions', 'select-buildings-new', 'send-message', 'confirm',
        ];
        this.steps = [
          { completed: false, text: 'Select Dates' },
          { completed: false, text: 'Select Push Type' },
          { completed: false, text: 'Select Requirement' },
          { completed: false, text: 'Select Positions' },
          { completed: false, text: 'Select Buildings' },
          { completed: false, text: 'Send Message' },
          { completed: false, text: 'Confirm' },
        ];
        break;
      default:
        this.screenArray = [
          'select-dates', 'select-push-params', 'select-requirement',
          'select-buildings-new', 'send-message', 'confirm',
        ];
        this.steps = [
          { completed: false, text: 'Select Dates' },
          { completed: false, text: 'Select Push Type' },
          { completed: false, text: 'Select Requirement' },
          { completed: false, text: 'Select Buildings' },
          { completed: false, text: 'Send Message' },
          { completed: false, text: 'Confirm' },
        ];
      }
    }

    // Restore completed state for steps the user has already passed
    const stepIdx = Math.max(0, this.screenArray.indexOf(this.currentStep));
    for (let i = 0; i < stepIdx; i++) {
      if (this.steps[i]) this.steps[i].completed = true;
    }
    this.selectedStep = this.steps[stepIdx] ?? this.steps[0];
  }
  // #endregion wizard

  // #region navigation
  goNext = () => {
    if (this.checkDisabled()) return;
    const currentIndex = this.screenArray.indexOf(this.currentStep);
    if (currentIndex < 0 || currentIndex >= this.screenArray.length - 1) return;

    if (this.steps[currentIndex]) this.steps[currentIndex].completed = true;
    if (this.steps[currentIndex + 1]) this.selectedStep = this.steps[currentIndex + 1];
    this.currentStep = this.screenArray[currentIndex + 1];

    if (this.currentStep === 'confirm') {
      this.capacityWarningAcknowledged = false;
    }
  }

  goBack = () => {
    if (this.currentStep === 'error') {
      this.currentStep = 'confirm';
      return;
    }
    const currentIndex = this.screenArray.indexOf(this.currentStep);
    if (currentIndex <= 0) {
      this.backToPrimaryPushList.emit();
      return;
    }
    if (this.steps[currentIndex]) this.steps[currentIndex].completed = false;
    if (this.steps[currentIndex - 1]) {
      this.steps[currentIndex - 1].completed = false;
      this.selectedStep = this.steps[currentIndex - 1];
    }
    this.currentStep = this.screenArray[currentIndex - 1];
  }

  checkDisabled = (): boolean => {
    switch (this.currentStep) {
    case 'select-dates':
      return this.selectedDates.length === 0 || !this.pushOption || !this.areAllDateTimesComplete;
    case 'select-push-params':
      return !this.selectedPushGroup;
    case 'select-requirement':
      return !this.coursePushPackage?.requirement?.requirement;
    case 'select-buildings-new':
      return !(this.coursePushPackage?.buildings?.length > 0);
    case 'select-positions':
      return !(this.coursePushPackage?.positions?.length > 0);
    case 'select-users':
      return (
        !(this.coursePushPackage?.users?.length > 0)
        || !this.coursePushPackage?.requirement?.requirement
      );
    default:
      return false;
    }
  }

  closeModal = (refresh = false) => {
    this.dialogRef.close(refresh ? 'reload' : undefined);
  }

  finishAndClose = () => this.closeModal(true);
  // #endregion navigation

  // #region step 1 — dates + times
  onDatesChanged = (dates: string[]) => {
    this.selectedDates = dates;

    const src = this.completeCourseSessionDetails;
    const defaultStartTime = this.sessionTimeToHHMM(src?.sessionStartDate);
    const defaultEndTime = this.sessionTimeToHHMM(src?.sessionEndDate);

    const existingByDate = new Map<string, DateTimeEntry[]>();
    for (const entry of this.dateEntries) {
      const arr = existingByDate.get(entry.date) ?? [];
      arr.push(entry);
      existingByDate.set(entry.date, arr);
    }

    const newEntries: DateTimeEntry[] = [];
    for (const date of dates) {
      const existing = existingByDate.get(date);
      if (existing && existing.length > 0) {
        newEntries.push(...existing);
      } else {
        newEntries.push({ date, startTime: defaultStartTime, endTime: defaultEndTime });
      }
    }
    this.dateEntries = newEntries;
  }

  removeDate = (date: string) => {
    this.dateEntries = this.dateEntries.filter((e) => e.date !== date);
    this.selectedDates = this.selectedDates.filter((d) => d !== date);
  }

  removeDateEntry = (entry: DateTimeEntry) => {
    const idx = this.dateEntries.indexOf(entry);
    if (idx < 0) return;
    const newEntries = this.dateEntries.filter((_, i) => i !== idx);
    this.dateEntries = newEntries;
    if (!newEntries.some((e) => e.date === entry.date)) {
      this.selectedDates = this.selectedDates.filter((d) => d !== entry.date);
    }
  }

  addSessionForDate = (date: string) => {
    const src = this.completeCourseSessionDetails;
    const defaultStartTime = this.sessionTimeToHHMM(src?.sessionStartDate);
    const defaultEndTime = this.sessionTimeToHHMM(src?.sessionEndDate);
    this.dateEntries = [ ...this.dateEntries, { date, startTime: defaultStartTime, endTime: defaultEndTime } ];
  }

  /**
   * Extracts the local wall-clock hours and minutes from a session datetime string returned
   * by the API and returns them as an HH:mm string suitable for <input type="time">.
   * Session times are stored as real UTC; we convert to the entity's local timezone so the
   * displayed default matches what the user sees in the rest of the catalog (same approach
   * as course-catalog.component → parseSessionTimeAsUtc + asTimeWithTimezone).
   */
  private sessionTimeToHHMM(value: string | Date | null | undefined): string | null {
    if (!value) return null;
    const raw = value instanceof Date ? value : String(value).trim();
    const d = raw instanceof Date
      ? raw
      : new Date(
        typeof raw === 'string' && !raw.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(raw)
          ? `${raw}Z`
          : raw,
      );
    if (isNaN(d.getTime())) return null;

    // Convert UTC → entity local timezone (mirrors course-catalog display logic)
    const tz = this.entityService.getLocalTimezone();
    if (tz) {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(d);
      const hour = parts.find((p) => p.type === 'hour')?.value;
      const minute = parts.find((p) => p.type === 'minute')?.value;
      if (hour && minute) {
        // Some locales return '24' for midnight; normalise to '00'
        const h = parseInt(hour, 10) % 24;
        return `${String(h).padStart(2, '0')}:${minute}`;
      }
    }

    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  private from24h(time: string | null): { hr: number; min: number; period: 'AM' | 'PM' } | null {
    if (!time) return null;
    const [ hStr, mStr ] = time.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    const period: 'AM' | 'PM' = h < 12 ? 'AM' : 'PM';
    return { hr: h % 12 || 12, min: m, period };
  }
  // #endregion step 1 — dates + times

  // #region select-new push package (mirrors push-to-group)
  updateCoursePushPackageParameter = (event: any, parameter: string) => {
    let newValue: any;

    if (parameter === 'requirement') {
      const req = this.completeCourseSessionDetails?.Course?.Requirements?.find(
        (r: any) => r.requirementId == event,
      );
      newValue = req
        ? { id: req.requirementId, requirement: req.requirement }
        : { id: 'portfolio', requirement: 'Portfolio/Unassigned' };
    }

    if (parameter === 'buildings') {
      if (this.coursePushPackage.buildings.find((b: any) => b.buildingName === event.target.value)) {
        if (this.buildingsinput) this.buildingsinput.nativeElement.value = '';
        return;
      }
      const newBuilding = this.entityBuildings.find((b: any) => b.buildingName === event.target.value);
      if (this.buildingsinput) this.buildingsinput.nativeElement.value = '';
      if (!newBuilding) return;
      newValue = [ ...this.coursePushPackage.buildings, newBuilding ];
    }

    if (parameter === 'positions') {
      if (this.coursePushPackage.positions.find((p: any) => p.positionTitle === event.target.value)) {
        if (this.positionsinput) this.positionsinput.nativeElement.value = '';
        return;
      }
      const newPosition = this.entityPositions.find((p: any) => p.positionTitle === event.target.value);
      if (this.positionsinput) this.positionsinput.nativeElement.value = '';
      if (!newPosition) return;
      newValue = [ ...this.coursePushPackage.positions, newPosition ];

      // For position+building push type, clear any selected buildings whenever
      // the position selection changes — the filtered building list may differ.
      if (this.selectedPushGroup === 'COURSE_PUSH_TO_POSITION_BUILDING_USERS') {
        this.coursePushPackage = { ...this.coursePushPackage, buildings: [] };
      }
    }

    if (parameter === 'users') {
      if (this.coursePushPackage.users.find((u: any) => u.fullName === event.target.value)) {
        if (this.userinput) this.userinput.nativeElement.value = '';
        return;
      }
      const newUser = this.users.find((u: any) => u.fullName === event.target.value);
      if (this.userinput) this.userinput.nativeElement.value = '';
      if (!newUser) return;
      newValue = [ ...this.coursePushPackage.users, newUser ];
    }

    this.coursePushPackage = { ...this.coursePushPackage, [parameter]: newValue };
  }

  removeBuilding = (building: any) => {
    this.coursePushPackage = {
      ...this.coursePushPackage,
      buildings: this.coursePushPackage.buildings.filter((b: any) => b !== building),
    };
  }

  removePosition = (position: any) => {
    const updatedPositions = this.coursePushPackage.positions.filter((p: any) => p !== position);
    this.coursePushPackage = {
      ...this.coursePushPackage,
      positions: updatedPositions,
      // Clear buildings when position changes for position+building push type
      // so the user re-selects from the updated filtered list.
      ...(this.selectedPushGroup === 'COURSE_PUSH_TO_POSITION_BUILDING_USERS'
        ? { buildings: [] }
        : {}),
    };
  }

  removeUser = (user: any) => {
    this.coursePushPackage = {
      ...this.coursePushPackage,
      users: this.coursePushPackage.users.filter((u: any) => u !== user),
    };
  }
  // #endregion select-new push package

  // #region submit
  private buildBatchDuplicatePayload(): ({
    sourceCourseId: number;
    dates: string[];
    dateEntries: Array<{
      date: string;
      startTimeHr: number;
      startTimeMin: number;
      startAmPm: 'AM' | 'PM';
      endTimeHr: number;
      endTimeMin: number;
      endAmPm: 'AM' | 'PM';
    }>;
    requirementId?: number;
      userIds?: number[];
      messageBody?: string;
      groupId?: number;
      positionIds?: number[];
      districtTimezone?: string;
      pushType?: string;
      clearPreRecordedOption?: boolean;
      copyExistingRegistrations?: boolean;
    }) | null {
    if (!this.sourceCourseId || !this.areAllDateTimesComplete) return null;

    const payload: {
      sourceCourseId: number;
      dates: string[];
      dateEntries: Array<{
        date: string;
        startTimeHr: number;
        startTimeMin: number;
        startAmPm: 'AM' | 'PM';
        endTimeHr: number;
        endTimeMin: number;
        endAmPm: 'AM' | 'PM';
      }>;
      requirementId?: number;
      userIds?: number[];
      messageBody?: string;
      groupId?: number;
      positionIds?: number[];
      districtTimezone?: string;
      pushType?: string;
      clearPreRecordedOption?: boolean;
      copyExistingRegistrations?: boolean;
    } = {
      sourceCourseId: Number(this.sourceCourseId),
      dates: this.sortedDateEntries.map((entry) => entry.date),
      districtTimezone: this.entityService.getLocalTimezoneWithFallback(),
      dateEntries: this.sortedDateEntries.map((entry) => {
        const start = this.from24h(entry.startTime)!;
        const end = this.from24h(entry.endTime)!;
        return {
          date: entry.date,
          startTimeHr: start.hr,
          startTimeMin: start.min,
          startAmPm: start.period,
          endTimeHr: end.hr,
          endTimeMin: end.min,
          endAmPm: end.period,
        };
      }),
    };

    // Scheduled duplicates cannot remain Async or Anytime.
    if (this.sourceIsAsyncOrAnytime) {
      payload.clearPreRecordedOption = true;
    }

    if (this.pushOption === 'copy-existing') {
      payload.copyExistingRegistrations = true;
    }

    if (this.pushOption === 'select-new') {
      if (this.selectedPushGroup) payload.pushType = this.selectedPushGroup;

      const reqId = this.coursePushPackage.requirement?.id;
      if (reqId && reqId !== 'portfolio') payload.requirementId = parseFloat(reqId);

      if (this.isPushToSelectedUsersGroup()) {
        const userIds = this.coursePushPackage.users
          .map((u: any) => parseFloat(u.id))
          .filter((id: number) => !isNaN(id));
        if (userIds.length > 0) payload.userIds = userIds;
        if (this.coursePushPackage.messageContent) {
          payload.messageBody = this.coursePushPackage.messageContent;
        }
      } else if (
        this.selectedPushGroup === 'COURSE_PUSH_TO_BUILDING_USERS'
        && this.coursePushPackage.buildings?.length > 0
      ) {
        payload.groupId = parseFloat(this.coursePushPackage.buildings[0].id);
        if (this.coursePushPackage.messageContent) {
          payload.messageBody = this.coursePushPackage.messageContent;
        }
      } else if (
        this.selectedPushGroup === 'COURSE_PUSH_TO_POSITION_USERS'
        && this.coursePushPackage.positions?.length > 0
      ) {
        payload.positionIds = this.coursePushPackage.positions
          .map((p: any) => parseFloat(p.id))
          .filter((id: number) => !isNaN(id));
        if (this.coursePushPackage.messageContent) {
          payload.messageBody = this.coursePushPackage.messageContent;
        }
      } else if (
        this.selectedPushGroup === 'COURSE_PUSH_TO_POSITION_BUILDING_USERS'
        && this.coursePushPackage.buildings?.length > 0
        && this.coursePushPackage.positions?.length > 0
      ) {
        payload.groupId = parseFloat(this.coursePushPackage.buildings[0].id);
        payload.positionIds = this.coursePushPackage.positions
          .map((p: any) => parseFloat(p.id))
          .filter((id: number) => !isNaN(id));
        if (this.coursePushPackage.messageContent) {
          payload.messageBody = this.coursePushPackage.messageContent;
        }
      } else if (this.coursePushPackage.messageContent) {
        payload.messageBody = this.coursePushPackage.messageContent;
      }
    }

    return payload;
  }

  submitBatch = () => {
    if (this.isSubmitting) return;
    if (!this.sourceCourseId || this.sortedDateEntries.length === 0) return;
    if (this.pushOption === 'select-new' && !this.selectedPushGroup) return;

    const payload = this.buildBatchDuplicatePayload();
    if (!payload) return;

    this.isSubmitting = true;
    this.submitErrorMessage = '';
    this.submitErrorDetails = [];
    this.duplicatedCourses = [];
    this.inactiveUsersSkipped = 0;
    this.currentStep = 'submitting';

    this.submitSubscription?.unsubscribe();
    this.submitSubscription = this.coursesService.batchDuplicateCourse(payload).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        this.duplicatedCourses = response?.data?.duplicatedCourses ?? [];
        this.inactiveUsersSkipped = response?.data?.inactiveUsersSkipped ?? 0;
        this.currentStep = 'success';
      },
      error: (error: any) => {
        this.isSubmitting = false;
        this.handleSubmitError(error);
      },
    });
  }

  retrySubmit = () => this.submitBatch();

  private handleSubmitError = (error: any) => {
    const errorBody = error?.error ?? error;
    const additional = errorBody?.additionalInfo ?? errorBody?.details ?? null;

    this.resultErrorCode = errorBody?.statusCode ?? error?.status ?? null;

    const detailRows: DateConflict[] = additional?.details ?? errorBody?.details ?? [];
    if (Array.isArray(detailRows) && detailRows.length > 0) {
      this.submitErrorDetails = detailRows.map((row: any) => ({
        date: row?.date ?? '',
        reason: this.translateErrorCode(row?.code, row?.reason),
        code: row?.code,
      }));
    } else {
      this.submitErrorDetails = [];
    }

    this.submitErrorMessage = this.translateTopLevelError(errorBody, error);
    this.currentStep = 'error';
  }

  private translateTopLevelError = (errorBody: any, rawError: any): string => {
    const httpStatus = rawError?.status;
    const code = errorBody?.statusCode;
    const message = errorBody?.message;

    if (httpStatus === 403 || code === 4007) {
      return 'You do not have permission to duplicate and push courses. Contact your administrator.';
    }
    if (httpStatus === 404 && /feature/i.test(String(message))) {
      return 'This feature is not enabled for your district. Reach out to support if you need access.';
    }
    if (httpStatus === 404) {
      return 'The source course or selected group could not be found. Refresh and try again.';
    }
    if (httpStatus === 409) {
      return 'One or more selected dates already have a course scheduled. Remove the conflicts and try again.';
    }
    if (httpStatus === 0 || (rawError?.name === 'HttpErrorResponse' && !httpStatus)) {
      return 'We couldn\'t reach the server. Check your connection and try again.';
    }
    return message || 'Something went wrong while creating the duplicates. Please try again.';
  }

  private translateErrorCode = (code: string | undefined, fallback: string | undefined): string => {
    switch (code) {
    case 'PAST_DATE': return 'Date is in the past';
    case 'INVALID_DATE': return 'Date format is invalid';
    case 'DUPLICATE_DATE': return 'Date appears more than once in your selection';
    default: return fallback || 'This date could not be processed';
    }
  }
  // #endregion submit
}
